import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import PageMeta from "@/components/PageMeta";
import SectionHeading from "@/components/SectionHeading";
import { IMAGES, LEADERSHIP } from "@/lib/constants";
import {
  ArrowUpRight,
  Shield,
  Flag,
  Heart,
  Users,
  GraduationCap,
  Building2,
  HeartHandshake,
  Sparkles,
  Flame,
  Compass,
  ScrollText,
} from "lucide-react";

const PILLARS = [
  { icon: GraduationCap, t: "Professional Development", b: "Preparing the next generation of Detroit firefighters through Project 100 mentorship, candidate guidance, and continued growth opportunities for active and retired members." },
  { icon: Heart, t: "Health & Wellness", b: "Caring for the well-being of Phoenix members — active and retired — through active and retired firefighter fellowship, family support, and connection to the Phoenix community." },
  { icon: Shield, t: "Equitable Inclusion", b: "Advocating for representation, opportunity, and access within the Detroit Fire Department, and serving as a voice for minority firefighters and the communities of Detroit." },
  { icon: HeartHandshake, t: "Community Engagement", b: "Serving Detroit through the annual backpack giveaway, senior feeding and fellowship, support for families impacted by fire, and stewardship of Historic Engine 11." },
];

const TIMELINE = [
  { year: "1883", title: "Historic Engine 11 Built", body: "Historic Engine 11 was built at 2733 Gratiot Avenue on Detroit's east side." },
  { year: "1967", title: "Phoenix is Founded", body: "Phoenix is founded in Detroit, following the events of 1967, to advocate for minority firefighters and serve as a liaison between the Detroit Fire Department and the community." },
  { year: "2002", title: "Phoenix Purchases Historic Engine 11", body: "After the building stood vacant for nearly twelve years, Phoenix officially purchases Historic Engine 11, establishing a permanent home for advocacy, mentorship, preservation, and service." },
  { year: "2027", title: "60 Years of Phoenix", body: "Phoenix marks 60 years of service, legacy, advocacy, and community impact." },
];

function LeaderCard({ person }) {
  const slug = person.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <div
      className="group rounded-2xl overflow-hidden bg-black/40 border border-white/5 hover:border-amber-500/40 transition-colors"
      data-testid={`leader-${slug}`}
    >
      <div className="relative aspect-square overflow-hidden bg-black">
        <img
          src={person.photo}
          alt={`${person.name} — ${person.title}, Phoenix of the Detroit Fire Department`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/70 via-black/15 to-transparent pointer-events-none" />
      </div>
      <div className="px-5 py-5">
        <div className="font-display text-lg sm:text-xl text-white leading-tight">
          {person.name}
        </div>
        <div className="mt-1.5 text-[11px] sm:text-xs tracking-[0.22em] uppercase font-accent text-amber-400">
          {person.title}
        </div>
      </div>
    </div>
  );
}

export default function About() {
  return (
    <div data-testid="about-page">
      <PageMeta
        title="About Phoenix"
        description="Founded 1967 in Detroit — a 501(c)(3) nonprofit advocating for minority firefighters, supporting Detroit communities, mentoring future candidates through Project 100, and stewarding Historic Engine 11."
        path="/about"
      />
      <PageHero
        overline="About · Established 1967"
        title="Phoenix of the Detroit Fire Department."
        subtitle="A 501(c)(3) nonprofit rooted in Detroit. A voice for minority firefighters. A partner to the community. A six-decade legacy still rising."
        image={IMAGES.aboutHero}
      >
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
          <Link to="/membership" className="btn-gold w-full sm:w-auto justify-center" data-testid="about-hero-member">
            Become a Member <ArrowUpRight className="h-4 w-4" />
          </Link>
          <Link to="/donate-sponsor" className="btn-primary w-full sm:w-auto justify-center" data-testid="about-hero-support">
            Support Our Mission
          </Link>
          <Link to="/programs-outreach" className="btn-ghost w-full sm:w-auto justify-center" data-testid="about-hero-programs">
            Programs &amp; Outreach
          </Link>
        </div>
        <div className="mt-5 text-xs sm:text-sm tracking-[0.18em] uppercase font-accent text-amber-400/90">
          International Association of Black Professional Firefighters · North Central Region
        </div>
      </PageHero>

      {/* ----- WHY PHOENIX SYMBOL CALLOUT ----- */}
      <section className="px-5 lg:px-8 -mt-6 lg:-mt-10" data-testid="why-phoenix">
        <div className="max-w-7xl mx-auto rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-6 lg:p-8 flex flex-col sm:flex-row sm:items-center gap-5">
          <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-500 text-black">
            <Flame className="h-6 w-6" strokeWidth={2.5} />
          </span>
          <div>
            <div className="overline">Why Phoenix?</div>
            <p className="mt-2 text-slate-100 text-base lg:text-lg leading-relaxed max-w-4xl">
              The Phoenix symbolizes rising from challenge with purpose, strength,
              and renewed commitment. For the Phoenix of the Detroit Fire
              Department, the name reflects a legacy of resilience, advocacy,
              service, and the belief that <span className="text-amber-400 italic">from the ashes, we rise.</span>
            </p>
          </div>
        </div>
      </section>

      {/* ----- OUR STORY ----- */}
      <section className="py-20 lg:py-28" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <SectionHeading overline="Our Story" title="Six decades of brotherhood, sisterhood, and service." />
            <div className="space-y-5 text-slate-300 text-lg leading-relaxed">
              <p>
                Phoenix of the Detroit Fire Department was founded in 1967
                following the events of that year. Our mission centers on
                assisting minorities with entering and advancing in the fire
                service and acting as a liaison between the Detroit Fire
                Department and the community we serve. Phoenix is composed of
                active and retired Detroit Fire Department members who work
                together to create a greater collective impact for our members
                and the community we serve and protect.
              </p>
              <p>
                Sixty years later, the mission stands. We are a voice inside the
                department, a partner to the community, and stewards of Historic
                Engine 11 — a Detroit landmark that anchors our work and our legacy.
              </p>
              <p>
                Through Project 100, outreach, scholarships, and active and retired firefighter support,
                the Phoenix carries forward the principle our name was built on:
                from the ashes, we rise to serve Detroit.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="card-dark p-8">
              <div className="overline">By the Numbers</div>
              <div className="mt-6 grid grid-cols-2 gap-6">
                {[
                  { n: "60", l: "Years in 2027" },
                  { n: "1967", l: "Founded in Detroit" },
                  { n: "ENG 11", l: "Historic Firehouse" },
                  { n: "Detroit", l: "Community served" },
                ].map((s) => (
                  <div key={s.l}>
                    <div className="font-accent text-amber-500 text-4xl lg:text-5xl leading-none">{s.n}</div>
                    <div className="text-slate-400 text-sm mt-2">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ----- MISSION / VISION / VALUES ----- */}
      <section className="py-20 lg:py-28 bg-[#0E0E0E]" data-testid="mvv">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading overline="Mission · Vision · Values" title="What guides us." />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="card-dark p-8" data-testid="mvv-mission">
              <Compass className="h-7 w-7 text-amber-500" />
              <div className="font-display text-2xl text-white mt-5">Mission</div>
              <p className="text-slate-300 mt-3 leading-relaxed">
                To serve as a voice for minority firefighters, a liaison between
                the Detroit Fire Department and the community, and a force for
                recruitment, mentorship, preservation, and service.
              </p>
            </div>
            <div className="card-dark p-8" data-testid="mvv-vision">
              <Sparkles className="h-7 w-7 text-amber-500" />
              <div className="font-display text-2xl text-white mt-5">Vision</div>
              <p className="text-slate-300 mt-3 leading-relaxed">
                To remain a strong, connected fire service community — supporting
                active Detroit firefighters, honoring retirees and Phoenix legacy
                leaders, preparing future candidates through Project 100,
                preserving Phoenix history, and standing with the Detroit
                community we serve.
              </p>
            </div>
            <div className="card-dark p-8" data-testid="mvv-values">
              <Heart className="h-7 w-7 text-amber-500" />
              <div className="font-display text-2xl text-white mt-5">Values</div>
              <p className="text-slate-300 mt-3 leading-relaxed font-accent tracking-wide">
                Service · Brotherhood · Sisterhood · Legacy · Advocacy · Community
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ----- MISSION PILLARS (6 cards) ----- */}
      <section className="py-20 lg:py-28" data-testid="about-pillars">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading overline="Our Mission in Practice" title="What we stand for." />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {PILLARS.map((p) => (
              <div key={p.t} className="card-dark p-8" data-testid={`pillar-${p.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <p.icon className="h-7 w-7 text-amber-500" />
                <h3 className="font-display text-xl text-white mt-5">{p.t}</h3>
                <p className="text-slate-400 mt-3 leading-relaxed text-sm">{p.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ----- TIMELINE ----- */}
      <section className="py-20 lg:py-28 bg-[#0E0E0E]" data-testid="about-timeline">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="Six Decades of Phoenix"
            title="A timeline of legacy."
            subtitle="From founding in 1967 to six decades of Detroit service."
          />
          <div className="mt-14 relative">
            <div className="absolute left-5 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-amber-500/50 via-amber-500/20 to-transparent md:-translate-x-px" />
            <ul className="space-y-10">
              {TIMELINE.map((m, i) => {
                const right = i % 2 === 1;
                return (
                  <li key={m.title} className="relative md:grid md:grid-cols-2 md:gap-12 items-center">
                    <span className="absolute left-5 md:left-1/2 -translate-x-1/2 top-2 md:top-1/2 md:-translate-y-1/2 h-4 w-4 rounded-full bg-amber-500 ring-4 ring-amber-500/20" />
                    <div className={`pl-14 md:pl-0 ${right ? "md:col-start-2 md:pl-12" : "md:pr-12 md:text-right"}`}>
                      <div className="card-dark p-6 inline-block w-full">
                        <div className={`flex items-baseline gap-3 ${right ? "" : "md:justify-end"}`}>
                          <span className="font-accent text-amber-500 text-3xl tracking-wider">{m.year}</span>
                        </div>
                        <div className="font-display text-xl text-white mt-2">{m.title}</div>
                        <p className="text-slate-400 mt-2 text-sm leading-relaxed">{m.body}</p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      {/* ----- HISTORIC ENGINE 11 CALLOUT ----- */}
      <section className="py-20 lg:py-28" data-testid="about-engine11">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="rounded-2xl overflow-hidden ring-ember">
              <img src={IMAGES.engine11} alt="Historic Engine 11 firehouse" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-6">
            <SectionHeading overline="The Home of the Phoenix Legacy" title="Historic Engine 11." />
            <p className="text-slate-300 text-lg leading-relaxed">
              Historic Engine 11 is more than a headquarters. It is a Detroit
              landmark, a gathering place, a preservation responsibility, and a
              living connection to the fire service legacy Phoenix continues to
              carry forward.
            </p>
            <div className="mt-8">
              <Link to="/historic-engine-11" className="btn-gold" data-testid="about-engine11-cta">
                Explore Historic Engine 11 <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ----- PROJECT 100 CALLOUT ----- */}
      <section className="py-20 lg:py-28" data-testid="about-project100">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden ring-ember p-8 lg:p-12">
            <img src={IMAGES.project100} alt="" className="absolute inset-0 w-full h-full object-cover object-top opacity-55" />
            <div className="absolute inset-0 bg-gradient-to-br from-black/45 via-black/25 to-red-950/25" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="overline">Project 100</div>
                <h3 className="font-display text-3xl lg:text-4xl text-white mt-3 leading-[1.05]">
                  Recruitment, Mentorship, and Preparation.
                </h3>
                <p className="text-slate-300 mt-5 leading-relaxed max-w-2xl">
                  Through Project 100, Phoenix supports candidates by helping them
                  understand the Detroit Fire Department pathway, prepare for
                  prerequisite steps, and connect with mentors who understand the
                  discipline and commitment required to pursue the fire service.
                </p>
                <p className="text-slate-400 text-xs lg:text-sm mt-4 max-w-2xl">
                  Eligibility, testing, hiring, training, and employment decisions
                  are determined by the Detroit Fire Department.
                </p>
              </div>
              <div className="lg:col-span-4 flex">
                <Link to="/programs-outreach#project-100" className="btn-gold w-full justify-center" data-testid="about-project100-cta">
                  Learn About Project 100 <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ----- BLACK FIREFIGHTERS MATTER — Phoenix identity statement (gallery-style, restrained) ----- */}
      {/* Surgical insertion: sits between Project 100 callout and Leadership.    */}
      {/* Phoenix identity piece. Do not duplicate on other pages.                 */}
      <section
        className="py-20 lg:py-32 bg-[#0A0A0A]"
        data-testid="about-bfm"
        aria-label="Black Firefighters Matter — Phoenix identity statement"
      >
        <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <div
            className="text-[12px] tracking-[0.32em] uppercase font-accent text-amber-500 mb-8"
            data-testid="bfm-overline"
          >
            Since 1967.
          </div>
          <figure
            className="relative mx-auto w-full max-w-[760px] aspect-[3/2] rounded-xl overflow-hidden"
            style={{
              boxShadow:
                "0 0 0 1px rgba(245,158,11,0.22), 0 30px 60px -20px rgba(0,0,0,0.8)",
            }}
          >
            <img
              src="/identity/black-firefighters-matter.jpg"
              alt="Black Firefighters Matter — Phoenix of the Detroit Fire Department"
              className="w-full h-full object-cover"
              loading="lazy"
              data-testid="bfm-graphic"
            />
          </figure>
        </div>
      </section>

      {/* ----- LEADERSHIP (Executive Board + Board of Trustees) ----- */}
      <section className="py-20 lg:py-28 bg-[#0E0E0E]" data-testid="about-leadership">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="Leadership"
            title="Meet the Phoenix board."
            subtitle="Phoenix is led by an elected Executive Board and Board of Trustees, alongside an active membership of Detroit firefighters, retirees, and community partners."
          />

          {/* ----- Executive Board ----- */}
          <div className="mt-14" data-testid="executive-board">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px flex-1 bg-gradient-to-r from-transparent to-amber-500/40" />
              <h3 className="font-accent text-xs sm:text-sm tracking-[0.32em] uppercase text-amber-400 whitespace-nowrap">
                Executive Board
              </h3>
              <span className="h-px flex-1 bg-gradient-to-l from-transparent to-amber-500/40" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {LEADERSHIP.executiveBoard.map((p) => (
                <LeaderCard key={p.name} person={p} />
              ))}
            </div>
          </div>

          {/* ----- Board of Trustees ----- */}
          <div className="mt-20" data-testid="board-of-trustees">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px flex-1 bg-gradient-to-r from-transparent to-amber-500/40" />
              <h3 className="font-accent text-xs sm:text-sm tracking-[0.32em] uppercase text-amber-400 whitespace-nowrap">
                Board of Trustees
              </h3>
              <span className="h-px flex-1 bg-gradient-to-l from-transparent to-amber-500/40" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
              {LEADERSHIP.boardOfTrustees.map((p) => (
                <LeaderCard key={p.name} person={p} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* (2027 bridge removed — evergreen About page; 2027 content lives on /2027 only) */}

      {/* ----- 501(c)(3) TRUST BAND ----- */}
      <section className="py-16 lg:py-20" data-testid="about-501c3">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="card-dark p-6 lg:p-8 flex flex-col sm:flex-row sm:items-center gap-5">
            <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400">
              <ScrollText className="h-6 w-6" />
            </span>
            <div className="flex-1">
              <div className="overline">501(c)(3) Nonprofit</div>
              <p className="text-slate-200 mt-2 leading-relaxed">
                The Phoenix of the Detroit Fire Department is a registered
                501(c)(3) nonprofit organization. All gifts are tax-deductible to
                the fullest extent allowed by law.
              </p>
            </div>
            <Link to="/donate-sponsor" className="btn-gold whitespace-nowrap" data-testid="about-501c3-cta">
              Support Our Mission <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ----- CLOSING CTA ----- */}
      <section className="py-24 lg:py-32" data-testid="about-closing">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <div className="overline">Join the Mission</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mt-3 leading-[1.05]">
            Stand with the
            <span className="block text-amber-500">Phoenix.</span>
          </h2>
          <div className="divider-gold mx-auto" />
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Link to="/membership" className="btn-gold w-full justify-center" data-testid="closing-member">Become a Member</Link>
            <Link to="/donate-sponsor" className="btn-primary w-full justify-center" data-testid="closing-support">Support Our Mission</Link>
            <Link to="/programs-outreach" className="btn-ghost w-full justify-center" data-testid="closing-programs">Programs &amp; Outreach</Link>
            <Link to="/contact" className="btn-ghost w-full justify-center" data-testid="closing-contact">Contact Phoenix</Link>
          </div>
          <div className="mt-10 flex items-center justify-center gap-3 text-xs text-slate-500">
            <Flame className="h-3.5 w-3.5 text-amber-500" />
            <span className="tracking-[0.28em] uppercase font-accent">Phoenix · Detroit · Est. 1967</span>
          </div>
        </div>
      </section>
    </div>
  );
}
